import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import { createM365User, listM365Users, deleteM365User } from '../../api/m365';
import '../../styles/M365Management.css';

const M365Management = () => {
  const [form, setForm] = useState({
    displayName: '',
    userPrincipalName: '',
    mailNickname: '',
    password: '',
    gmail: ''
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load existing M365 users
  const loadUsers = useCallback(async () => {
    try {
      const data = await listM365Users();
      setUsers(data);
    } catch (error) {
      console.error('Error loading M365 users', error);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-fill mailNickname when userPrincipalName changes
    if (name === 'userPrincipalName') {
      const nickname = value.includes('@') ? value.split('@')[0] : value;
      setForm((prev) => ({
        ...prev,
        [name]: value,
        mailNickname: nickname
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Create M365 user + send Gmail welcome email
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    try {
      const payload = {
        displayName: form.displayName,
        userPrincipalName: form.userPrincipalName,
        mailNickname: form.mailNickname,
        password: form.password,
        gmail: form.gmail
      };

      const response = await createM365User(payload);

      if (response && response.success) {
        setSuccessMessage(
          `✅ Microsoft 365 account created for ${form.displayName}. A welcome email has been sent to ${form.gmail}.`
        );

        setForm({
          displayName: '',
          userPrincipalName: '',
          mailNickname: '',
          password: '',
          gmail: ''
        });

        await loadUsers();
      } else {
        setSuccessMessage(`⚠️ Failed to create user: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating user', error);
      setSuccessMessage('❌ Error creating user.');
    } finally {
      setLoading(false);
    }
  };

  // Delete M365 user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this Microsoft 365 user?')) return;

    try {
      await deleteM365User(userId);
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="m365-management-container">
        <h2>Microsoft 365 User Management</h2>

        {/* Success Message */}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* Create User Form */}
        <form className="m365-form" onSubmit={handleCreateUser}>
          <input
            type="text"
            name="displayName"
            placeholder="Display Name"
            value={form.displayName}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="userPrincipalName"
            placeholder="Microsoft Email (e.g. john@yourdomain.com)"
            value={form.userPrincipalName}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Temporary Password"
            value={form.password}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="gmail"
            placeholder="Gmail to send welcome email"
            value={form.gmail}
            onChange={handleChange}
            required
          />

          <button type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create M365 User'}
          </button>
        </form>

        {/* Existing Users Table */}
        <table className="m365-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.displayName}</td>
                  <td>{user.userPrincipalName}</td>
                  <td>
                    <button onClick={() => handleDeleteUser(user.id)}>Delete</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3">No Microsoft 365 users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
};

export default M365Management;
